import type { ItemPollingOps, ItemPollingResponse } from '@mimir/client-common/polling/itemPollingOps';
import { approximateFractionValue } from '@mimir/common/util/integerMath';
import type { ObservableState } from '@mimir/common/util/observable';
import { createPeaksManagerFromAudioProxies } from '@mimir/web/components/Common/Timeline/Audio/separateAudioProxiesAudioManager';
import { BasicPeaksManager } from '@mimir/web/model/basicPeaksManager';
import type { AudioInitializationCallbacks } from '@mimir/web/model/itemMediaPlayerOps';
import type { PeaksManagerOps } from '@mimir/web/model/peaksManagerOps';

const ALL_CHANNELS_TOTAL_DURATION_LIMIT_IN_MS = 3 * 60 * 60 * 1000;


/**
 * This interface exposes the data and action needed when loading
 * peaks from .m4a proxies, since we need some limits in order to
 * avoid memory exhaustion problems.
 */
export interface PeaksFromM4aProxiesConfig {
  cumulatedAudioProxyDurationInMs: ObservableState<number>;
  markAudioProxiesTooLargeToLoad: () => void;
}


export function loadPeaks(
  itemId: string,
  itemPollingOps: ItemPollingOps,
  addPollingListenerId: (id: string) => void,
  setPeaksManager: (m: PeaksManagerOps) => void,
  peaksFromM4aProxiesConfig: PeaksFromM4aProxiesConfig | null,
  peakCount: ObservableState<number>,
  callbacks: AudioInitializationCallbacks,
): void {
  return loadPeaksInternal(itemId, itemPollingOps, addPollingListenerId, setPeaksManager, peaksFromM4aProxiesConfig, peakCount, callbacks, createPeaksManagerFromAudioProxies);
}


/**
 * This function is responsible for trying to load peaks, either
 * from the peaks we generate ourselves, or from .m4a audio proxies if
 * peaksFromM4aProxiesConfig is not null.
 */
export function loadPeaksInternal(
  itemId: string,
  itemPollingOps: ItemPollingOps,
  addPollingListenerId: (id: string) => void,
  setPeaksManager: (m: PeaksManagerOps) => void,
  peaksFromM4aProxiesConfig: PeaksFromM4aProxiesConfig | null,
  peakCount: ObservableState<number>,
  callbacks: AudioInitializationCallbacks,
  createPeaksManagerFromAudioProxiesFn: (
    channelUrls: string[],
    peakCount: ObservableState<number>,
    callbacks: AudioInitializationCallbacks,
  ) => Promise<PeaksManagerOps>,
): void {
  let peaksManager: PeaksManagerOps | undefined;
  let loadingPeaksFromAudioProxies = false;

  const listenerId = itemPollingOps.addPollListener(
    itemId,
    ['peaks', 'proxyAudioTrackUrls', 'playableDuration'],
    (response: null | Pick<ItemPollingResponse, 'peaks' | 'proxyAudioTrackUrls' | 'playableDuration'>, pollingStopped: boolean) => {
      if (response === null || response.playableDuration === null) {
        return;
      }

      // If we have a BasicPeaksManager and peaks, let's feed these peaks to
      // the manager
      if (peaksManager instanceof BasicPeaksManager) {
        if (response.peaks !== null) {
          peaksManager.setPeaks(response.peaks.peaksPerChannel);
        }
        return;
      }

      // If we don't have a peaks manager yet, let's see if we can create one

      if (response.peaks !== null) {
        // If we have peaks, let's create a manager
        const peaksManagerV2 = new BasicPeaksManager(response.peaks.nPeaksPerSecondPerChannel, response.peaks.peaksPerChannel, peakCount);
        setPeaksManager(peaksManagerV2);
        peaksManager = peaksManagerV2;

        // When we start to get any peaks, we want to show them
        callbacks.showPeaks();
        return;
      }

      if (!loadingPeaksFromAudioProxies && peaksFromM4aProxiesConfig !== null && response.proxyAudioTrackUrls !== null) {
        loadingPeaksFromAudioProxies = true;
        const proxyAudioTrackUrlsLength = response.proxyAudioTrackUrls.length;
        const audioProxyDurationInMs = approximateFractionValue(response.playableDuration) * 1000 * proxyAudioTrackUrlsLength;
        const newDuration = audioProxyDurationInMs + peaksFromM4aProxiesConfig.cumulatedAudioProxyDurationInMs.currentValue();
        if (newDuration > ALL_CHANNELS_TOTAL_DURATION_LIMIT_IN_MS) {
          // Loading the proxies for this item would go over the quota
          peaksFromM4aProxiesConfig.markAudioProxiesTooLargeToLoad();
          itemPollingOps.removePollListener(listenerId);
          return;
        }

        peaksFromM4aProxiesConfig.cumulatedAudioProxyDurationInMs.value = newDuration;

        void createPeaksManagerFromAudioProxiesFn(response.proxyAudioTrackUrls, peakCount, callbacks).then(manager => {
          setPeaksManager(manager);
          itemPollingOps.removePollListener(listenerId);
        });
        return;
      }

    },
  );
  addPollingListenerId(listenerId);
}
